/* eslint-disable react-hooks/rules-of-hooks */
import camelcaseKeys from "camelcase-keys";
import Cookie from "js-cookie";
import React, { useEffect, useRef, useState } from "react";
import { Provider } from "react-redux";
import { storageFactory } from "storage-factory";
import { logException } from "@wss/error-tracking";
import { toError } from "@wss/error-tracking/utils";
import { Throwable } from "@wss/error-tracking/throwable";
import { usePageData } from "@wss/react";
import { useComponentStore } from "@wss/store";
import { useCookies } from "react-cookie";
import GoodBetterBestBanner from "~/ProductDetails/SpotlightBanner/GoodBetterBest";
import SideBySideBanner from "~/ProductDetails/SpotlightBanner/SideBySide";
import { BannerType } from "~/ProductDetails/SpotlightBanner/shared/definitions";
import NotificationCenter from "~/common/Notification/NotificationCenter";
import ResourcesAndDownloads from "~/common/ResourcesAndDownloads/ResourcesAndDownloads";
import { getTotalRatings } from "~/common/Reviews/CustomerReviewHelper";
import Reviews from "~/common/Reviews/Reviews";
import Store from "~/common/Store";
import { User } from "~/common/Store/models/User";
import { defaultUserObject } from "~/common/Store/rootReducer";
import useOnClickOutside from "~/common/hooks/useOnClickOutside";
import useIsMounted from "~/common/useIsMounted";
import getCarouselPlacement from "./Carousel/getCarouselPlacement";
import CompatibleModels from "./CompatibleModels";
import CustomersAlsoViewedCarousel from "./CustomersAlsoViewedCarousel";
import CustomersAlsoViewedItemSection from "./CustomersAlsoViewedItemSection";
import EquivalentItems from "./EquivalentItems";
import EverlivingProductDetailPricingBox from "./EverlivingProductDetailPricingBox";
import FindRelatedProducts from "./FindRelatedProducts";
import InternalNotesButton from "./InternalNotes/InternalNotesButton";
import InternalNotesModal from "./InternalNotes/InternalNotesModal";
import OtherProductsInThisLineCarousel from "./OtherProductsInThisLineCarousel";
import getMinBuyForQuantityDropdown from "./Pricing/AddToCart/getMinBuyForQuantityDropdown";
import ProductBreakdowns from "./ProductBreakdowns";
import ProductCutDownModal from "./ProductCutDown/ProductCutDownModal";
import ProductDetailBrandSideSection from "./ProductDetailBrandSideSection";
import ProductDetailHeading from "./ProductDetailHeading";
import ProductDetailHighlightsAndMetaSideSection from "./ProductDetailHighlightsAndMetaSideSection";
import { ProductDetailPageResult, ProductDetailTemplateGroup, Templates } from "./ProductDetailPageResult";
import ProductDetailPricingBox from "./ProductDetailPricingBox";
import ProductDetailProductAds from "./ProductDetailProductAds";
import ProductDetailSampling from "./ProductDetailSampling";
import ProductVariations from "./ProductVariations";
import RecommendationCarousel from "./RecommendationCarousel";
import RemoveOpengraphMetadataFromDocument from "./RemoveOpengraphMetadataFromDocument";
import ReplacementsAd from "./ReplacementsAd";
import RequestToQuote from "./RequestToQuote";
import Schemas from "./Schemas/Schemas";
import SeeMoreProductsCarousel from "./SeeMoreProductsCarousel";
import SocialShareSection from "./SocialShareSection";
import SpecificationTable from "./SpecificationTable";
import StickyHeader from "./StickyHeader";
import TallCarouselNoCartButton from "./TallCarouselNoCartButton";
import ExpandedDescriptionTemplateDetailSection from "./Templates/ExpandedDescriptionTemplateDetailSection";
import OutletHeadingLabel from "./Templates/OutletTemplate/OutletHeadingLabel";
import OutletPricingBox from "./Templates/OutletTemplate/OutletPricingBox";
import StandardTemplateDetailSection from "./Templates/StandardTemplateDetailSection";
import YouMayAlsoNeedCarousel from "./YouMayAlsoNeedCarousel";
import { ProductDetailsGlobalContext } from "./useGlobalContext";
import { VirtualGroupingProvider } from "./useVirtualGroupingContext";
import MediaViewerV2 from "~/common/MediaViewerV2";
import RelatedItemsCarousel from "./RelatedItems/RelatedItemsCarousel";
import { homepage } from "~/common/Constants/Constants";
import ChildOrNull from "~/common/ChildOrNull";
import { productPageView } from "~/analytics/PageView/pageViewHelper";
import { ProductDetailsContext, ProductDetailsStore } from "./ProductDetailsStore";
import FeatureFlagProvider from "~/common/contexts/FeatureFlagProvider";
import SimilarProductsContainer from "./SimilarProducts/Container";
import tracking from "~/Gtm/Tracking/tracking";
import updateVariationIds from "~/Gtm/updateVariationIds";
import LoginModal from "./Pricing/LoginModal";
import { VariationIds } from "~/common/globals";
const getStartBuy = (template?: ProductDetailPageResult) => template && Math.max(template.minimumBuy, template.mustBuy) || 1;
const quantitySelectExpression: (item?: ProductDetailPageResult) => boolean = item => !!(item && (item.minimumBuy > 1 || item.mustBuy > 1));
export function useProductDetailPageData(): ProductDetailTemplateGroup {
  return usePageData("ProductDetails");
}
const ProductDetails = () => {
  const {
    productDetailGroupingSelect,
    productTemplates,
    isGoogleView,
    creditKeyConfig,
    virtualGroupOptionsMap = [],
    groupMembers = [],
    isUtmMediumCPC = false,
    featureFlags = {}
  } = useProductDetailPageData();
  const localStorage = storageFactory(() => window.localStorage);
  const store = useComponentStore<ProductDetailsStore>(() => ({
    isGoogleView: isGoogleView || false
  }));
  const [activeItemNumberIndex, setActiveItemNumberIndex] = useState(0);
  const [showInternalNotesModal, setShowInternalNotesModal] = useState(false);
  const [showCpcDisplayMinimalVersion, setShowCpcDisplayMinimalVersion] = useState(false);
  const [isZipcodeEligibleForInstallationService, setIsZipcodeEligibleForInstallationService] = useState<boolean>(false);
  const [showSpotlightBanner, setShowSpotlightBanner] = useState(true);
  const mounted = useIsMounted();
  const activeTemplate = productTemplates[activeItemNumberIndex];
  if (activeTemplate) {
    activeTemplate.groupingAttributes.groupMembers = groupMembers;
  }
  const [cookies] = useCookies(["pageVersion", "pageVariation"]);
  useEffect(() => {
    if (cookies.pageVariation && cookies.pageVersion) {
      setShowCpcDisplayMinimalVersion(cookies.pageVersion === "minimal");

      // GTM tracking
      updateVariationIds([{
        featureName: VariationIds.Cpc_AB_Test,
        featureOption: cookies.pageVariation === "B" ? "1" : "0" // 50/50 A/B test
      }]);
    }
  }, [cookies]);
  const minimalPageVersion = activeTemplate?.isCpcMinimalPageVersion && showCpcDisplayMinimalVersion;
  const minimalPageVersionDynamicText = activeTemplate?.variationMembership && activeTemplate?.variationMembership.variationGroups.length > 0 ? "More Options Available" : "View Full Product Details";
  const setActiveBreadcrumbDisplay = (heading: string) => {
    if (heading === "") {
      return;
    }
    const breadcrumb = document.getElementById("breadCrumbCurrent");
    if (breadcrumb) {
      breadcrumb.innerText = heading;
    }
  };
  const getBannerType = (showGoodBetterBestBanner: boolean, hasSideBySideProductCompare: boolean) => {
    if (hasSideBySideProductCompare) {
      return BannerType.SideBySide;
    }
    if (showGoodBetterBestBanner) {
      return BannerType.GoodBetterBest;
    }
    return "";
  };
  const getSpotlightBanner = (bannerType: string) => {
    if (activeTemplate && bannerType === BannerType.GoodBetterBest) {
      return <GoodBetterBestBanner itemNumber={activeTemplate.itemNumber} toggleSpotlightBanner={(enabled: boolean) => setShowSpotlightBanner(enabled)} user={defaultUserObject} mounted />;
    }
    const hideSideBySideBanner = activeTemplate?.sideBySideProductCompareItem?.displayType === "U" && !activeTemplate?.sideBySideProductCompareItem?.isRecommendedSimilarItem;
    if (activeTemplate && bannerType === BannerType.SideBySide && !hideSideBySideBanner) {
      return <SideBySideBanner originalLinkingItemNumber={activeTemplate.itemNumber} sideBySideProductCompareItem={activeTemplate.sideBySideProductCompareItem} user={defaultUserObject} mounted />;
    }
    return null;
  };
  useEffect(() => {
    if (creditKeyConfig?.publicKeyId && !Cookie.get("CreditKeyPublicKey")) {
      Cookie.set("CreditKeyPublicKey", creditKeyConfig.publicKeyId);
    }
    if (creditKeyConfig?.minimumDisplayPrice && !Cookie.get("CreditKeyPdpMinimumDisplayPrice")) {
      Cookie.set("CreditKeyPdpMinimumDisplayPrice", creditKeyConfig.minimumDisplayPrice.toString());
    }
  });
  useEffect(() => {
    async function importCarouselFunctionality() {
      if (document.querySelector("[data-component=carousel]")) {
        const {
          default: initCategoryCarousels
        } = await import("~/common/initCategoryCarousels");
        initCategoryCarousels();
      }
    }
    importCarouselFunctionality();
  }, [activeItemNumberIndex]);
  const showStickyHeaderKey = "showStickyHeaderFlag";
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [stickyHeaderCollapsed, setStickyHeaderCollapsed] = useState(localStorage.getItem(showStickyHeaderKey) === "true");
  const [addToCartSuccess, setAddToCartSuccess] = useState(false);
  const handleOutletAddToCart = () => {
    setOutletItemInCart(true);
    setAddToCartSuccess(true);
  };
  const handleStickyHeaderCollapse = () => {
    const collapsed = !stickyHeaderCollapsed;
    localStorage.setItem(showStickyHeaderKey, collapsed.toString());
    setStickyHeaderCollapsed(collapsed);
  };
  useEffect(function initIntersectionObserverEffect() {
    function handleIntersections(entries: IntersectionObserverEntry[]) {
      setShowStickyHeader(!entries[0]?.isIntersecting);
    }
    const observer = new IntersectionObserver(handleIntersections);
    const element = document.querySelector("#subject");
    if (element) observer.observe(element);
    return function cleanupIntersectionObserver() {
      observer.disconnect();
    };
  }, [activeTemplate]);
  if (!activeTemplate) {
    return null;
  }
  const newProduct = !!activeTemplate?.isNewProduct;
  const [hasQuantitySelect, setHasQuantitySelect] = useState(quantitySelectExpression(activeTemplate));
  const startBuy = getStartBuy(activeTemplate);
  const [quantity, setQuantity] = useState(hasQuantitySelect || startBuy > 1 ? "0" : startBuy.toString());
  const handleQuantityChanged = (value: string) => {
    if (value === "other") {
      setHasQuantitySelect(false);
      setQuantity(getMinBuyForQuantityDropdown({
        minBuy: activeTemplate.minimumBuy,
        mustBuy: activeTemplate.mustBuy
      }).toString());
      return;
    }
    setQuantity(value);
  };
  const handleVirtualGroupingSelectChange = (itemNumber: string) => {
    if (itemNumber === "") {
      setActiveItemNumberIndex(0);
      return productTemplates[0]?.link ?? "";
    }
    const activeIndex = productTemplates.findIndex(p => p.itemNumber === itemNumber);
    setActiveItemNumberIndex(activeIndex);
    setActiveBreadcrumbDisplay(productTemplates[activeIndex]?.header ?? "");
    setHasQuantitySelect(quantitySelectExpression(productTemplates[activeIndex]));
    setQuantity(getStartBuy(productTemplates[activeIndex]).toString());
    return productTemplates[activeIndex]?.link ?? "";
  };
  const displayPricingBoxForTemplate = (activeTemplate: ProductDetailPageResult, user: User) => {
    const {
      itemNumber,
      outletAttributes,
      template,
      showEverlivingProductPage,
      allowOutOfStockPurchase,
      isRequestAQuoteEnabled
    } = activeTemplate;
    if (template === Templates.OutletTemplate) {
      return <OutletPricingBox product={activeTemplate} outletAttributes={outletAttributes} itemInCart={outletItemInCart} onAddToCart={handleOutletAddToCart} user={user} mounted={mounted} />;
    }
    if (showEverlivingProductPage) {
      return <EverlivingProductDetailPricingBox product={activeTemplate} />;
    }
    if (isRequestAQuoteEnabled) {
      return <RequestToQuote newProduct={newProduct} requestAQuoteUrl={activeTemplate.requestAQuoteUrl} />;
    }
    return <ProductDetailPricingBox key={`pricingBox-${itemNumber}`} mounted={mounted} product={activeTemplate} quantity={quantity} hasQuantitySelect={hasQuantitySelect} onQuantityChanged={handleQuantityChanged} user={defaultUserObject} handleVirtualGroupingSelectChange={handleVirtualGroupingSelectChange} setAddToCartSuccess={setAddToCartSuccess} allowOutOfStockPurchase={allowOutOfStockPurchase} minimalPageVersion={minimalPageVersion} data-sentry-element="ProductDetailPricingBox" data-sentry-component="displayPricingBoxForTemplate" data-sentry-source-file="index.tsx" />;
  };
  const [outletItemInCart, setOutletItemInCart] = useState(false);
  useEffect(() => {
    if (!activeTemplate?.outletAttributes?.outletProductId) {
      return;
    }
    type ShoppingCartItem = {
      outletItemId: number;
      isOutletItem: number;
      itemNumber: string;
    };
    async function getShoppingCartItems(): Promise<Throwable> {
      const endpointUrl = "/api/shoppingcart/items";
      try {
        const response = await fetch(endpointUrl);
        const result = await response.json<ShoppingCartItem[]>();
        const cartItems = camelcaseKeys(result);
        if (cartItems.find(c => c.outletItemId === activeTemplate?.outletAttributes?.outletProductId)) {
          setOutletItemInCart(true);
        }
        return {
          isError: false
        };
      } catch (e) {
        const error = new Error("Error in getShoppingCartItems", {
          cause: toError(e)
        });
        await logException(error);
        setOutletItemInCart(false);
        return {
          error,
          isError: true
        };
      }
    }
    getShoppingCartItems();
  }, [activeTemplate?.outletAttributes?.outletProductId]);
  useEffect(() => {
    if (activeTemplate && activeTemplate.itemNumber) {
      productPageView(activeTemplate.itemNumber);
    }
  }, [activeTemplate]);
  if (activeTemplate && productTemplates[0]) {
    const {
      actualVendorItemNumber,
      breadCrumbs,
      originalId,
      productImages,
      productImageDisclaimer,
      primaryBrand,
      defaultCategory,
      itemNumber,
      title,
      unitOfMeasure,
      unitsPerPackaging,
      specificationTable,
      highlights,
      hasPartsOrAccessories,
      stockMessage,
      isQuickShip,
      videos,
      modelFiles,
      otherProductsFromThisLine,
      customersAlsoViewedCarouselItems,
      youMayAlsoNeedCarouselItems,
      otherProductsCarouselPromotion,
      price,
      productCutDown,
      link,
      compatibleModelItems,
      compatibleModelItemsCount,
      variationMembership,
      equivalentItems,
      productDocuments,
      productDisclaimer,
      linkedResources,
      manufacturerItemNumberDisplayText = "",
      breakdownOptions = [],
      header,
      showGoodBetterBestBanner,
      qualityRatingMembership,
      template,
      productAds,
      isVirtualPart,
      seeMoreProductsCarouselItems,
      showSeeMoreProductsCarousel,
      showEverlivingProductPage,
      showVendorItemNumber,
      relatedSearchTerms,
      compatibleWithLink,
      youMayAlsoNeedUrl,
      weightInPounds,
      sampleProgram,
      description
    } = activeTemplate;
    const hasMedia = Boolean(productImages?.length > 0 || videos?.length > 0 || modelFiles);
    const {
      customerReviews
    } = productTemplates[0];
    const showSampleProduct = sampleProgram && sampleProgram.isActive && sampleProgram.sampleProduct.isInStock;
    const carouselPlacement = getCarouselPlacement(activeTemplate);
    const isVirtualGroupMaster = activeTemplate.groupingAttributes.isGroupMaster && activeTemplate.groupingAttributes.isVirtual;
    const activeGroupMember = groupMembers?.find(groupMember => {
      return groupMember.itemNumber === activeTemplate.itemNumber;
    });
    const hasAmountSelection = activeGroupMember?.configurationOptions?.some(option => {
      return option.name === "Amount";
    });
    const clonedItem = productTemplates?.find(x => x.itemNumber === originalId);
    let selectedVariationItemNumber = itemNumber;
    if (isVirtualGroupMaster) {
      selectedVariationItemNumber = activeTemplate.originalId;
    }
    const hasVariations = variationMembership && variationMembership.variationGroups && !(variationMembership.variationGroups.length === 1 && variationMembership.variationGroups[0]?.variationGroupItems.length === 1);
    const socialLink = isVirtualGroupMaster && clonedItem?.link || link;
    const socialTitle = isVirtualGroupMaster && clonedItem?.header || header;
    const isExpandedDescriptionTemplate = template === Templates.ExpandedDescriptionTemplate;
    const isOutletTemplate = template === Templates.OutletTemplate;
    const bannerType = getBannerType(showGoodBetterBestBanner && !(Cookie.get("doNotShowGBB") === "true"), activeTemplate.sideBySideProductCompareItem?.hasSideBySideProductCompare);
    const internalNotesRef = useRef(null);
    useOnClickOutside(internalNotesRef, () => setShowInternalNotesModal(false));
    const displayAskButton = !isVirtualGroupMaster && !isOutletTemplate && !showEverlivingProductPage;
    const hasQuantityVariations = hasVariations && variationMembership?.variationGroups?.some(group => group.optionName === "Quantity");
    const displayBreakdownOptions = breakdownOptions && breakdownOptions.length > 1 && !isVirtualGroupMaster && !hasAmountSelection && !hasQuantityVariations;
    const shouldShowRelatedItemsCarousel = !minimalPageVersion && !showEverlivingProductPage && defaultCategory?.id && defaultCategory?.name && breadCrumbs?.length > 0;
    const handleFullProductDetailsClick = () => {
      window.location.assign(window.location.pathname);
    };
    return <ProductDetailsContext.Provider value={store}>
        <Provider store={Store}>
          <FeatureFlagProvider featureFlags={featureFlags}>
            <RemoveOpengraphMetadataFromDocument user={defaultUserObject} mounted={mounted} />
            <ProductDetailsGlobalContext.Provider value={{
            isZipcodeEligibleForInstallationService,
            setIsZipcodeEligibleForInstallationService
          }}>
              <VirtualGroupingProvider virtualGroupOptionsMap={virtualGroupOptionsMap}>
                {!showEverlivingProductPage && <StickyHeader product={activeTemplate} quantity={quantity} hasQuantitySelect={hasQuantitySelect} onQuantityChanged={handleQuantityChanged} show={showStickyHeader} collapsed={stickyHeaderCollapsed} onClose={handleStickyHeaderCollapse} setAddToCartSuccess={setAddToCartSuccess} addToCartSuccess={addToCartSuccess} isGoogleView={isGoogleView} alreadyInCart={outletItemInCart} mounted={mounted} customerReviews={customerReviews} minimalPageVersion={minimalPageVersion} />}
                <div className={`container pdp-v3 ${showEverlivingProductPage ? `container--everliving` : ``}`} data-component="storefront-product-detail">
                  <InternalNotesButton showInternalNotesModal={showInternalNotesModal} toggleInternalNotesModal={() => setShowInternalNotesModal(!showInternalNotesModal)} user={defaultUserObject} mounted={mounted} itemNumber={originalId} />
                  {showInternalNotesModal && <InternalNotesModal outletItemNumber={activeTemplate.itemNumber} toggleInternalNotesModal={() => setShowInternalNotesModal(!showInternalNotesModal)} isVirtual={activeTemplate.groupingAttributes.isVirtual} itemNumber={isVirtualGroupMaster || isOutletTemplate ? originalId : itemNumber} groupItemNumber={activeTemplate.groupingAttributes.virtualGroupItemNumber} groupingItemNumbers={groupMembers.map(item => item.itemNumber.toUpperCase())} internalNotesRef={internalNotesRef} />}
                  {showSpotlightBanner && bannerType && !showEverlivingProductPage && !isOutletTemplate && !isUtmMediumCPC && !showSeeMoreProductsCarousel && getSpotlightBanner(bannerType)}
                  <ProductDetailHeading itemNumber={originalId} descriptionHtml={header} rating={customerReviews.overallRating} numberOfReviews={getTotalRatings(customerReviews.productReviewDetails).length} showVendorItemNumber={showVendorItemNumber} actualVendorItemNumber={actualVendorItemNumber} isEverLiving={showEverlivingProductPage} isOutlet={isOutletTemplate} numberOfAnswers={activeTemplate.questionsAndAnswers?.length ?? 0} isGroupMaster={activeTemplate.groupingAttributes.isGroupMaster} minimalPageVersion={minimalPageVersion} />
                  {showSeeMoreProductsCarousel && <SeeMoreProductsCarousel seeMoreProductsCarouselItems={seeMoreProductsCarouselItems} category={defaultCategory} isEverliving={showEverlivingProductPage} />}
                  {isOutletTemplate && <OutletHeadingLabel />}
                  <div id="page" role="main" className={`product-page${isExpandedDescriptionTemplate ? " expanded-detail" : ""}`}>
                    {!showEverlivingProductPage && !isOutletTemplate && productCutDown && productAds && <ProductCutDownModal productCutDown={productCutDown} productName={title} productItemNumber={originalId.toLocaleUpperCase()} productPrice={price.price} isVirtual={activeTemplate.groupingAttributes.isVirtual} isVirtualGroupMaster={isVirtualGroupMaster} productDetailGroupingSelect={productDetailGroupingSelect} handleVirtualGroupingSelectChange={handleVirtualGroupingSelectChange} selectedItemNumber={activeTemplate.groupingAttributes.isGroupMaster ? "" : itemNumber} hasQuantityDiscounts={price.hasQuantityDiscounts} quantityDiscountPrice={price.hasQuantityDiscounts ? price.quantityDiscounts[0]?.price : 0} quantityDiscountQty={price.hasQuantityDiscounts ? price.quantityDiscounts[0]?.startQuantity : 0} mounted={mounted} />}
                    <section id="gallery" data-no-jquery aria-label="gallery">
                      {hasMedia && <MediaViewerV2 alt="alt" images={productImages} videos={isOutletTemplate ? [] : videos} videoTitle="video title" modelFiles={modelFiles} productImageDisclaimer={productImageDisclaimer} activeItemNumberIndex={activeItemNumberIndex} title={title} styles={minimalPageVersion ? "bg-white border-gray-200 rounded border-solid border border-l-0 border-r-0 xs:border-l xs:border-r flex flex-wrap overflow-hidden p-5 relative md:mb-5 lt:mb-0 xl:flex-nowrap" : undefined} />}
                      {!minimalPageVersion && <SocialShareSection url={`http://www.webstaurantstore.com${socialLink}`} media={`https://cdnimg.webstaurantstore.com/${productImages[0]?.large}`} description={socialTitle} hasGallery={hasMedia} titleSection={carouselPlacement.showYMANCarousel} youMayAlsoNeedUrl={youMayAlsoNeedUrl} displayAskButton={displayAskButton} />}
                    </section>
                    <div className="-order-3 lt:order-none lt:float-right lt:clear-right">
                      {price && displayPricingBoxForTemplate(activeTemplate, defaultUserObject)}
                      <div className={`side-col aside new-exp ${minimalPageVersion ? "pt-0 xs:pt-5 md:pt-0" : ""}`}>
                        {/* Show CAV as side tile if exactly 1 item in the collection. */}
                        {customersAlsoViewedCarouselItems?.length === 1 && customersAlsoViewedCarouselItems[0] && <CustomersAlsoViewedItemSection customersAlsoViewedItem={customersAlsoViewedCarouselItems[0]} originalLinkingItemNumber={itemNumber} />}
                        {!minimalPageVersion && (!!displayBreakdownOptions || hasVariations) && <nav aria-label="product variations" className="pb-0 xs:pb-5">
                              <div className="box padded flex flex-col gap-4">
                                <ChildOrNull condition={!!displayBreakdownOptions}>
                                  <ProductBreakdowns itemNumber={itemNumber} breakdownOptions={breakdownOptions} />
                                </ChildOrNull>
                                <ChildOrNull condition={hasVariations}>
                                  <ProductVariations itemNumber={selectedVariationItemNumber} variationMembership={variationMembership} isVirtualGroupMaster={!!isVirtualGroupMaster} />
                                </ChildOrNull>
                              </div>
                            </nav>}
                        {sampleProgram && showSampleProduct && <ProductDetailSampling maxQuantityPerOrder={sampleProgram.maxQuantityPerOrder} maxQuantityPerCustomer={sampleProgram.maxQuantityPerCustomer} mounted={mounted} labelType={sampleProgram.labelType} product={{
                        description: sampleProgram.sampleProduct.title,
                        feedIdentifier: sampleProgram.sampleProduct.feedIdentifier,
                        imagePath: sampleProgram.sampleProduct.productImages[0]?.thumbnail,
                        itemNumber: sampleProgram.sampleItemNumber,
                        itemNumberId: sampleProgram.sampleItemNumberId,
                        link: sampleProgram.sampleProduct.link,
                        isInStock: sampleProgram.sampleProduct.isInStock,
                        price: sampleProgram.sampleProduct.price.price,
                        minimumBuy: sampleProgram.sampleProduct.minimumBuy,
                        mustBuy: sampleProgram.sampleProduct.mustBuy,
                        startBuy: sampleProgram.sampleProduct.startBuy,
                        isWebstaurantPlusEligible: sampleProgram.sampleProduct.isWebstaurantPlusEligible,
                        plusPrice: sampleProgram.sampleProduct.price.minimumAdvertisedPriceOverride.priceLevel5
                      }} user={defaultUserObject} />}
                        {(stockMessage?.length > 0 || isQuickShip || highlights?.length > 0 || highlights?.length > 0 || qualityRatingMembership || isOutletTemplate) && <ProductDetailHighlightsAndMetaSideSection isOutlet={isOutletTemplate} mounted={mounted} product={activeTemplate} />}
                        {!isVirtualPart && primaryBrand.showBrandTile && <ProductDetailBrandSideSection primaryBrand={primaryBrand} defaultCategory={defaultCategory} />}
                        {minimalPageVersion && <div className="m-4 xs:m-0">
                            <tracking.Button theme="wss" size="large" aria-label={minimalPageVersionDynamicText} full onClick={() => handleFullProductDetailsClick()} className="white border border-solid text-green-500 !border-green-500 hover:bg-gray-100 hover:text-green-500" data-track="cpc_pdp_view_full_product_details" data-track-ei={itemNumber} data-testid="view-full-pdp-button">
                              {minimalPageVersionDynamicText}
                            </tracking.Button>
                          </div>}
                      </div>
                    </div>
                    {carouselPlacement.showYMANCarousel && <YouMayAlsoNeedCarousel key={`ymanCarousel-${itemNumber}`} carouselItems={youMayAlsoNeedCarouselItems} youMayAlsoNeedUrl={youMayAlsoNeedUrl} />}
                    {carouselPlacement.showRecAiCarousel && <RecommendationCarousel key={`recommendationCarousel-${itemNumber}`} product={activeTemplate} mounted={mounted} />}
                    <div className="main-col">
                      {carouselPlacement.showCAVCarousel && <CustomersAlsoViewedCarousel key={`customersAlsoViewedCarousel-${itemNumber}`} carouselItems={customersAlsoViewedCarouselItems} itemNumber={itemNumber} />}
                      {carouselPlacement.showOPFTLCarouselAboveDescription && <OtherProductsInThisLineCarousel key={`otherProductsCarousel-${itemNumber}`} carouselItems={otherProductsFromThisLine} otherProductsCarouselPromotionMember={otherProductsCarouselPromotion} />}
                      {!minimalPageVersion && (isExpandedDescriptionTemplate ? <ExpandedDescriptionTemplateDetailSection activeTemplate={activeTemplate} user={defaultUserObject} mounted={mounted} /> : <StandardTemplateDetailSection activeTemplate={activeTemplate} />)}
                    </div>
                    <div className="side-col aside xs:mb-4">
                      {isExpandedDescriptionTemplate && productAds && <ProductDetailProductAds ads={productAds} isExpandedDescriptionTemplate />}
                      {hasPartsOrAccessories && !isVirtualGroupMaster && <ReplacementsAd link={compatibleWithLink} manufacturerItemNumberDisplayText={manufacturerItemNumberDisplayText} />}
                      {(productDocuments?.length > 0 || productDisclaimer?.warrantyInformation && productDisclaimer?.showWarrantyInformation || linkedResources && linkedResources.length > 0) && <ResourcesAndDownloads manufacturerItemNumberDisplayText={manufacturerItemNumberDisplayText} productDocuments={productDocuments} productDisclaimer={productDisclaimer} linkedResources={linkedResources} />}
                      {isExpandedDescriptionTemplate && <SpecificationTable itemNumber={itemNumber} specifications={specificationTable?.specifications ?? []} name={specificationTable?.name ?? ""} unitOfMeasure={unitOfMeasure} unitsPerPackaging={unitsPerPackaging} showInSidebar weightInPounds={weightInPounds} />}
                      {compatibleModelItems?.length > 0 && <CompatibleModels compatibleModelItems={compatibleModelItems} compatibleModelItemsCount={compatibleModelItemsCount} itemNumber={itemNumber} />}
                      {equivalentItems?.length > 0 && <EquivalentItems equivalentItems={equivalentItems} />}
                    </div>
                    {carouselPlacement.showOPFTLCarouselBelowDescription && <TallCarouselNoCartButton key={`otherProductsTallCarousel-${itemNumber}`} id="other-products-from-this-line-carousel" carouselItems={otherProductsFromThisLine} carouselPromotionMember={otherProductsCarouselPromotion} heading="Other Products from this Line" dataComponentName="other-products-from-this-line-carousel" gtmCarouselName="Other Products from this Line" supportsRequestQuoteProgram />}
                    {shouldShowRelatedItemsCarousel && <RelatedItemsCarousel key={`relatedItemsTallCarousel-${itemNumber}`} itemNumber={itemNumber} itemTitle={title} categoryId={defaultCategory.id.toString()} categoryTitle={defaultCategory.name} breadcrumbs={breadCrumbs} />}
                    {!minimalPageVersion && <Reviews brand={primaryBrand.name} user={defaultUserObject} itemNumber={itemNumber} reviews={customerReviews} mounted={mounted} isVirtualGroupMaster={isVirtualGroupMaster} vgMasterOriginalItemNumber={activeTemplate.originalId} productDetailGroupingSelect={productDetailGroupingSelect} handleVirtualGroupingSelectChange={handleVirtualGroupingSelectChange} showOverview isEverliving={showEverlivingProductPage} image={productImages.find(image => image.isPrimaryDetailImage)?.thumbnail ?? "/images/products/new/imagecomingsoon_tn.jpg"} description={description} productLink={activeTemplate?.link ?? homepage} />}
                    {!isOutletTemplate && !showEverlivingProductPage && relatedSearchTerms?.length > 0 && <FindRelatedProducts relatedSearchTerms={relatedSearchTerms} />}
                  </div>
                  {minimalPageVersion && <SimilarProductsContainer key="similar-products" product={activeTemplate} mounted={mounted} user={defaultUserObject} />}
                  <NotificationCenter />
                </div>
                <Schemas activeProduct={activeTemplate} user={defaultUserObject} mounted={mounted} />
                <LoginModal />
              </VirtualGroupingProvider>
            </ProductDetailsGlobalContext.Provider>
          </FeatureFlagProvider>
        </Provider>
      </ProductDetailsContext.Provider>;
  }
  return null;
};
export default ProductDetails;